import { Text, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useEffect } from "react";
import ActionCard from "src/components/ActionCard";
import FormCard from "src/components/FormCard";
import NumberInput from "src/components/NumberInput/NumberInput";
import Select from "src/components/Select";
import Switch from "src/components/Switch/Switch";
import {
  useGetOrganizationSettingsLazyQuery,
  useUpdateOrganizationSettingsMutation,
  SeriesGenInterval,
} from "src/generated/graphql";
import useWritePermission from "src/hooks/usePermission";
import { CardRadius, EMPLOYEE_ROLE_OBJ, HeightCard } from "src/utils/constants";
import { number, object, string } from "yup";

type IProps = {
  organizationId?: string;
};

type IFormProps = {
  freezeExtendsTerm: boolean;
  seriesGenInterval: SeriesGenInterval;
  seriesGenRange: number;
};

const formInitialValues = {
  freezeExtendsTerm: false,
  seriesGenInterval: SeriesGenInterval.Weeks,
  seriesGenRange: 2,
};

const OrganizationSettings = ({ organizationId }: IProps) => {
  const theme = useMantineTheme();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgSettings);
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const color = theme.colors.secondaryTextColor[4];

  const [getOrganizationSettings, { data, loading: getLoading }] =
    useGetOrganizationSettingsLazyQuery({
      onCompleted() {
        resetFormState();
      },
      fetchPolicy: "network-only",
    });

  const resetFormState = () => {
    if (data?.organizationSettingsOne) {
      let { freezeExtendsTerm, seriesGenInterval, seriesGenRange } =
        data?.organizationSettingsOne;
      form.setValues({
        freezeExtendsTerm: freezeExtendsTerm!,
        seriesGenInterval,
        seriesGenRange,
      });
    }
  };

  const [updateSettings, { loading: updateLoading }] =
    useUpdateOrganizationSettingsMutation({
      onCompleted() {},
    });

  const requestFormSchema = object().shape({
    seriesGenInterval: string().required("Interval is required"),
    seriesGenRange: number().min(2, "Range should atleast be 2"),
  });

  const form = useForm({
    initialValues: formInitialValues,
    validate: yupResolver(requestFormSchema),
  });

  const handleUpdate = async (values: IFormProps) => {
    updateSettings({
      variables: {
        data: values,
        id: data?.organizationSettingsOne?.id!,
      },
    });
  };

  useEffect(() => {
    organizationId &&
      getOrganizationSettings({
        variables: {
          organizationId,
        },
      });
  }, []);

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  return (
    <FormCard
      title={"Organization Settings"}
      onSave={form.onSubmit((values) => handleUpdate(values))}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onCancel={handleCancel}
      disabled={getLoading || updateLoading}
      loading={getLoading || updateLoading}
      isFormActive={isFormActive}
    >
      <ActionCard
        title={"Agreement / Subscription"}
        mt={"sm"}
        height={HeightCard.xs}
        radius={CardRadius.xs}
        size="md"
      >
        <Switch
          labelPosition="left"
          label="Freeze Extends Term"
          checked={form.getInputProps("freezeExtendsTerm").value}
          {...form.getInputProps("freezeExtendsTerm")}
          mt="xs"
          disabled={isFormActive}
        />
      </ActionCard>

      <ActionCard
        title={" Meeting Series"}
        mt={"md"}
        height={HeightCard.xs}
        radius={CardRadius.xs}
        size="md"
      >
        <Select
          mt={"xs"}
          withAsterisk
          data={
            Object.keys(SeriesGenInterval)?.map((val) => ({
              value: val,
              label: val,
            })) || []
          }
          label="Series Interval"
          {...form.getInputProps("seriesGenInterval")}
          disabled={isFormActive}
        />

        <NumberInput
          mt={"xs"}
          withAsterisk
          label="Series Range"
          {...form.getInputProps("seriesGenRange")}
          disabled={isFormActive}
        />
      </ActionCard>
    </FormCard>
  );
};

export default OrganizationSettings;
